import { computed, ref, type Ref } from 'vue'
import { DateTime } from 'luxon'
import { SalesReportDate } from '../models/SalesReportDate'
import type { SalesReportSettingsType } from '../SalesReport'
import type { SalesReport } from '../models/SalesReport'

const periodDuration: Record<SalesReportSettingsType, Record<string, number>> = {
  daily: { days: 1 },
  weekly: { weeks: 1 },
  monthly: { months: 1 },
  yearly: { years: 1 },
  custom: {}
}

export function usePeriodShift(report: Ref<SalesReport | undefined>, transitionPrefix = 'cr') {
  const periodTransition = ref(`${transitionPrefix}-label-slide-left`)

  const periodOffset = computed({
    get: () => report.value?.periodOffset ?? 0,
    set: (val: number) => {
      if (report.value) report.value.periodOffset = val
    }
  })

  const reportType = computed<SalesReportSettingsType>(() => report.value?.type ?? 'daily')

  const shiftedDate = computed(() => {
    const type = reportType.value
    if (type === 'custom') return DateTime.now()
    const duration = periodDuration[type]
    return DateTime.now().plus(
      Object.fromEntries(
        Object.entries(duration).map(([k, v]) => [k, v * periodOffset.value])
      )
    )
  })

  const periodLabel = computed(() => {
    const type = reportType.value
    if (type === 'custom') return ''
    const srd = new SalesReportDate(shiftedDate.value, type)
    return srd.formatBoundaries()
  })

  const shiftPeriod = (direction: number) => {
    const newOffset = periodOffset.value + direction
    if (newOffset > 0) return
    periodTransition.value = direction < 0
      ? `${transitionPrefix}-label-slide-left`
      : `${transitionPrefix}-label-slide-right`
    periodOffset.value = newOffset
  }

  const resetPeriod = () => {
    periodTransition.value = `${transitionPrefix}-label-slide-right`
    periodOffset.value = 0
  }

  /**
   * Apply the current period offset to the report's date.
   * Updates the report's internal date and resets the previous date.
   * Returns the shiftedDate so callers can use it if needed.
   */
  const applyShift = () => {
    if (!report.value) return null
    const type = report.value.type
    if (type === 'custom') return null

    const date = periodOffset.value === 0 ? DateTime.now() : shiftedDate.value
    report.value.setDate(date)
    report.value.resetPreviousDate()
    return date
  }

  return {
    periodOffset,
    periodTransition,
    periodLabel,
    shiftedDate,
    shiftPeriod,
    resetPeriod,
    applyShift
  }
}
